import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/base/Loading";
import { formatNumber } from "@/lib/format";
import type { PlatformStats } from "@/pages/home/hooks/useHomeData";

interface OverviewCardsProps {
  stats: PlatformStats | null;
  loading: boolean;
}

type Tone = "primary" | "accent" | "secondary";

const toneClasses: Record<Tone, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-700",
  secondary: "bg-secondary-100 text-secondary-900",
};

interface CardItem {
  key: string;
  label: string;
  value: number | undefined;
  icon: string;
  tone: Tone;
  sub: string;
  to?: string;
  cta?: string;
}

export default function OverviewCards({ stats, loading }: OverviewCardsProps) {
  const { t } = useTranslation();
  const cards: CardItem[] = [
    {
      key: "active",
      label: t("overview.active"),
      value: stats?.active_licenses,
      icon: "ri-file-shield-2-line",
      tone: "primary",
      sub: t("overview.activeSub", { total: formatNumber(stats?.total_licenses) }),
    },
    {
      key: "pending",
      label: t("overview.pending"),
      value: stats?.pending_applications,
      icon: "ri-hourglass-2-line",
      tone: "accent",
      sub: t("overview.pendingSub"),
    },
    {
      key: "issued",
      label: t("overview.issued"),
      value: stats?.issued_this_month,
      icon: "ri-file-add-line",
      tone: "primary",
      sub: t("overview.issuedSub"),
    },
    {
      key: "expiring",
      label: t("overview.expiring"),
      value: stats?.expiring_soon,
      icon: "ri-alarm-warning-line",
      tone: "accent",
      sub: t("overview.expiringSub"),
      to: "/admin?tab=licenses&filter=expiring&window=30",
      cta: t("overview.expiringCta"),
    },
    {
      key: "customers",
      label: t("overview.customers"),
      value: stats?.total_customers,
      icon: "ri-team-line",
      tone: "secondary",
      sub: t("overview.customersSub"),
      to: "/admin/customers",
      cta: t("overview.customersCta"),
    },
    {
      key: "pendingCustomers",
      label: t("overview.pendingCustomers"),
      value: stats?.pending_customers,
      icon: "ri-user-follow-line",
      tone: "accent",
      sub: t("overview.pendingCustomersSub"),
      to: "/admin/customers?filter=pending",
      cta: t("overview.pendingCustomersCta"),
    },
    {
      key: "seats",
      label: t("overview.seats"),
      value: stats?.online_seats,
      icon: "ri-computer-line",
      tone: "primary",
      sub: t("overview.seatsSub"),
    },
  ];

  return (
    <section id="overview" className="scroll-mt-20 bg-background-100">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 md:px-6 md:py-20">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((card, index) => {
            const content = (
              <>
                <div className="flex items-center justify-between">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-md ${toneClasses[card.tone]}`}>
                    <i className={`${card.icon} text-xl`} />
                  </span>
                  <span className="flex h-6 w-6 items-center justify-center text-foreground-300">
                    <i className={card.to ? "ri-arrow-right-line" : "ri-arrow-right-up-line"} />
                  </span>
                </div>
                <p className="mt-4 font-heading text-3xl font-semibold tracking-tight text-foreground-950">
                  {loading ? <Skeleton className="h-8 w-24" /> : formatNumber(card.value)}
                </p>
                <p className="mt-1 text-sm font-medium text-foreground-800">{card.label}</p>
                <p className="mt-1 text-xs text-foreground-500">{card.sub}</p>
                {card.to && (
                  <p className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-accent-700">
                    {card.cta ?? t("help.viewDetail")}
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-arrow-right-line" />
                    </span>
                  </p>
                )}
              </>
            );

            const baseClass = "lh-fade-up block rounded-lg border bg-background-50 p-5 transition-colors";
            const style = { animationDelay: `${index * 0.06}s` };

            if (card.to) {
              return (
                <Link
                  key={card.key}
                  to={card.to}
                  style={style}
                  className={`${baseClass} cursor-pointer border-accent-200 hover:border-accent-400`}
                >
                  {content}
                </Link>
              );
            }

            return (
              <div
                key={card.key}
                style={style}
                className={`${baseClass} border-background-200 hover:border-primary-300`}
              >
                {content}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}